import { useState } from 'react';
import { BookOpen, ChevronDown, ChevronUp, HelpCircle, FileText, Users, ShoppingCart, Package, BarChart3, CreditCard, Wallet, Settings, TrendingUp, CheckCircle, Info } from 'lucide-react';

export default function GuidePage() {
  const [openSections, setOpenSections] = useState({
    overview: true,
    dashboard: false,
    inventory: false,
    purchasing: false,
    sales: false,
    finance: false,
    reports: false,
    system: false,
  });

  const toggleSection = (section) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const guideSections = [
    {
      id: 'overview',
      title: '📋 Ikhtisar Penggunaan Stokku',
      subtitle: 'Apa itu Stokku dan bagaimana ini membantu bisnis Anda',
      icon: BookOpen,
      content: [
        'Stokku adalah aplikasi manajemen akuntansi dan inventory terpadu untuk memudahkan operasional bisnis Anda.',
        'Aplikasi ini dirancang khusus untuk mengelola seluruh siklus operasional mulai dari pembelian barang, penjualan, hingga pelaporan keuangan.',
        'Dengan antarmuka yang ramah pengguna dan fitur lengkap, Stokku membantu Anda meningkatkan efisiensi, mengurangi kesalahan, dan membuat keputusan bisnis yang lebih baik.',
      ]
    },
    {
      id: 'dashboard',
      title: '📊 Cara Menggunakan Dashboard',
      subtitle: 'Memahami tampilan utama dan fitur-fitur penting',
      icon: TrendingUp,
      content: [
        'Dashboard adalah halaman utama yang menampilkan ringkasan operasional bisnis Anda.',
        'Berikut adalah komponen-komponen utama di dashboard:',
        '• Indikator Kinerja Utama (KPI) - Menampilkan ringkasan penjualan, pembelian, stok, dan keuangan',
        '• Grafik Tren - Menampilkan grafik penjualan dan pengeluaran dalam periode tertentu',
        '• Status Stok - Menampilkan jumlah item dengan stok rendah, hampir habis, dan habis',
        '• Notifikasi Terbaru - Menampilkan notifikasi penting seperti pesanan pembelian yang perlu diproses',
        '• Aktivitas Terbaru - Menampilkan aktivitas terbaru dalam sistem',
      ]
    },
    {
      id: 'inventory',
      title: '📦 Panduan Inventory & Stok',
      subtitle: 'Mengelola persediaan barang dengan efisien',
      icon: Package,
      content: [
        'Manajemen Inventory di Stokku mencakup seluruh siklus persediaan:',
        '1. Penerimaan Barang (Barang Masuk)',
        '   • Menerima barang dari supplier',
        '   • Memverifikasi kuantitas dan kualitas barang',
        '   • Memperbarui stok secara otomatis',
        '   • Membuat catatan audit',
        '',
        '2. Pengeluaran Barang (Barang Keluar)',
        '   • Mengeluarkan barang untuk penjualan atau transfer',
        '   • Melacak penggunaan stok',
        '   • Membuat catatan pengeluaran',
        '',
        '3. Transfer Stok',
        '   • Memindahkan barang antar gudang',
        '   • Melacak mutasi barang',
        '   • Mengupdate stok di kedua lokasi',
        '',
        '4. Stock Opname',
        '   • Melakukan penghitungan stok fisik',
        '   • Membandingkan dengan stok sistem',
        '   • Menyesuaikan jika ada perbedaan',
        '',
        '5. Kartu Stok',
        '   • Melihat riwayat transaksi setiap barang',
        '   • Melacak mutasi stok per satuan',
        '   • Menganalisis pola persediaan',
      ]
    },
    {
      id: 'purchasing',
      title: '🛒 Panduan Pembelian & Supplier',
      subtitle: 'Mengelola proses pembelian dengan efisien',
      icon: ShoppingCart,
      content: [
        'Manajemen Pembelian mencakup seluruh proses pembelian barang:',
        '1. Purchase Request (Permintaan Pembelian)',
        '   • Membuat permintaan pembelian barang',
        '   • Menyebutkan jumlah dan alasan kebutuhan',
        '   • Memperoleh persetujuan dari atasan atau departemen terkait',
        '',
        '2. Purchase Order (Pesanan Pembelian)',
        '   • Membuat pesanan pembelian ke supplier',
        '   • Menyebutkan detail produk, jumlah, harga, dan tanggal pengiriman',
        '   • Memperoleh konfirmasi dari supplier',
        '',
        '3. Penerimaan Barang (Penerimaan)',
        '   • Menerima barang sesuai dengan Purchase Order',
        '   • Memverifikasi kuantitas, kualitas, dan dokumentasi',
        '   • Memperbarui stok dan mencatat penerimaan',
        '',
        '4. Invoice Pembelian',
        '   • Mencatat faktur dari supplier',
        '   • Memverifikasi jumlah dan harga',
        '   • Membayar faktur yang sudah diterima',
        '',
        '5. Retur Pembelian',
        '   • Mengembalikan barang yang cacat atau tidak sesuai',
        '   • Memperbarui stok',
        '   • Mendapatkan kredit atau penggantian',
      ]
    },
    {
      id: 'sales',
      title: '💰 Panduan Penjualan & Pelanggan',
      subtitle: 'Mengelola proses penjualan dari awal hingga akhir',
      icon: CreditCard,
      content: [
        'Manajemen Penjualan mencakup seluruh siklus penjualan:',
        '1. Transaksi Baru (Penjualan)',
        '   • Membuat faktur penjualan untuk pelanggan',
        '   • Memilih produk dan jumlah yang diinginkan',
        '   • Menerapkan diskon dan pajak',
        '   • Membayar atau membuat piutang',
        '',
        '2. Riwayat Penjualan',
        '   • Melihat riwayat semua transaksi penjualan',
        '   • Mencari transaksi berdasarkan tanggal, pelanggan, atau produk',
        '   • Mengecek status pembayaran',
        '',
        '3. Invoice Penjualan',
        '   • Mencatat faktur penjualan',
        '   • Melacak pembayaran piutang',
        '   • Membuat pengingat pembayaran',
        '',
        '4. Retur Penjualan',
        '   • Menerima pengembalian dari pelanggan',
        '   • Memproses pengembalian uang atau kredit',
        '   • Memperbarui stok',
        '',
        '5. Piutang Usaha',
        '   • Melacak uang yang belum diterima dari pelanggan',
        '   • Mengirim pengingat pembayaran',
        '   • Membuat laporanAging Piutang',
      ]
    },
    {
      id: 'finance',
      title: '💼 Panduan Keuangan & Akuntansi',
      subtitle: 'Mengelola keuangan dan akuntansi bisnis Anda',
      icon: Wallet,
      content: [
        'Manajemen Keuangan mencakup seluruh aspek akuntansi:',
        '1. Kas & Bank',
        '   • Melacak semua transaksi kas dan bank',
        '   • Membuat penerimaan dan pengeluaran kas',
        '   • Menyiapkan rekonsiliasi bank',
        '',
        '2. Utang Usaha',
        '   • Mencatat semua utang kepada supplier',
        '   • Melacak tanggal jatuh tempo',
        '   • Membayar utang tepat waktu',
        '',
        '3. Piutang Usaha',
        '   • Melacak semua uang yang harus diterima dari pelanggan',
        '   • Mengirim pengingat pembayaran',
        '   • Membebankan bunga jika terlambat',
        '',
        '4. Chart of Accounts',
        '   • Mengelola semua akun akuntansi',
        '   • Mengelompokkan akun untuk pelaporan',
        '   • Menyesuaikan kode akun sesuai kebutuhan',
        '',
        '5. Jurnal',
        '   • Mencatat semua transaksi keuangan',
        '   • Membalik jurnal secara otomatis',
        '   • Membuat laporan buku besar',
        '',
        '6. Periode Akuntansi',
        '   • Mengelola periode akuntansi',
        '   • Membatalkan dan membuka periode',
        '   • Melindungi data akuntansi',
      ]
    },
    {
      id: 'reports',
      title: '📈 Panduan Laporan & Analisis',
      subtitle: 'Menganalisis data bisnis dan membuat keputusan yang tepat',
      icon: BarChart3,
      content: [
        'Manajemen Laporan membantu Anda menganalisis data dan membuat keputusan:',
        '1. Laporan Keuangan',
        '   • Laba Rugi - Menampilkan pendapatan, biaya, dan keuntungan',
        '   • Neraca - Menampilkan aset, kewajiban, dan ekuitas',
        '   • Perubahan Modal - Menampilkan perubahan ekuitas pemilik',
        '',
        '2. Laporan Operasional',
        '   • Laporan Penjualan - Analisis penjualan per produk, pelanggan, periode',
        '   • Laporan Pembelian - Analisis pembelian per supplier, periode',
        '   • Laporan Persediaan - Analisis stok dan nilai persediaan',
        '',
        '3. Laporan Aging',
        '   • Aging Piutang - Analisis piutang yang belum dibayar per usia',
        '   • Aging Utang - Analisis utang yang belum dibayar per usia',
        '',
        '4. Laporan Lainnya',
        '   • Buku Besar - Detail semua transaksi per akun',
        '   • Neraca Saldo - Ringkasan saldo semua akun',
        '   • Jurnal Penyesuaian - Mencatat penyesuaian akuntansi',
      ]
    },
    {
      id: 'system',
      title: '⚙️ Panduan Pengaturan & Sistem',
      subtitle: 'Mengelola pengaturan dan konfigurasi sistem',
      icon: Settings,
      content: [
        'Manajemen Sistem mencakup pengaturan dan konfigurasi aplikasi:',
        '1. Pengaturan Umum',
        '   • Informasi Perusahaan - Mengatur nama, alamat, kontak',
        '   • Preferensi Mata Uang - Mengatur mata uang dan format',
        '   • Preferensi Gudang - Mengatur gudang default',
        '',
        '2. Manajemen Pengguna',
        '   • Pengguna - Mengelola akun pengguna',
        '   • Roles - Mengelola izin dan akses',
        '   • Notifikasi - Mengelola pengaturan notifikasi',
        '',
        '3. Pengaturan Lanjutan',
        '   • Audit Log - Melacak semua aktivitas sistem',
        '   • Pengaturan Pajak - Mengelola konfigurasi pajak',
        '   • Integrasi - Menghubungkan dengan sistem lain',
        '',
        '4. Fitur Lainnya',
        '   • Penanganan Error - Mengelola pesan dan respons error',
        '   • Backup & Restore - Membuat cadangan data',
        '   • Pemeliharaan - Membersihkan data tidak perlu',
      ]
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card">
        <div className="card-header">
          <h1 className="text-2xl font-semibold text-surface-900 dark:text-white flex items-center gap-3">
            <div className="w-10 h-10 bg-primary-600 rounded-xl flex items-center justify-center">
              <BookOpen size={20} className="text-primary-900" />
            </div>
            Panduan Penggunaan Stokku
          </h1>
          <p className="text-surface-600 dark:text-surface-400 mt-2">
            Panduan lengkap untuk menggunakan aplikasi manajemen akuntansi dan inventory Stokku
          </p>
        </div>
      </div>

      {/* Guide Sections */}
      <div className="space-y-4">
        {guideSections.map((section) => {
          const Icon = section.icon;
          const isOpen = openSections[section.id];
          
          return (
            <div key={section.id} className="card hover:shadow-lg transition-shadow duration-200">
              <div className="w-full flex items-center justify-between p-6 text-left hover:bg-surface-50 dark:hover:bg-surface-800/50 transition-colors rounded-lg">
                <button
                  type="button"
                  onClick={() => toggleSection(section.id)}
                  aria-expanded={isOpen}
                  className="flex min-w-0 flex-1 items-center gap-4 text-left"
                >
                  <div className="w-12 h-12 bg-primary-50 ring-1 ring-primary-100 dark:bg-primary-950 dark:ring-primary-900 rounded-xl flex items-center justify-center">
                    <Icon size={24} className="text-primary-900 dark:text-primary-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-surface-900 dark:text-white mb-1">
                      {section.title}
                    </h2>
                    <p className="text-sm text-surface-600 dark:text-surface-400">
                      {section.subtitle}
                    </p>
                  </div>
                </button>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // Handle help action
                    }}
                    type="button"
                    aria-label={`Bantuan: ${section.title}`}
                    className="p-2 text-surface-400 hover:text-primary-900 dark:hover:text-primary-400 transition-colors"
                    title="Bantuan"
                  >
                    <HelpCircle size={20} />
                  </button>
                  {isOpen ? (
                    <ChevronUp size={20} className="text-surface-400" />
                  ) : (
                    <ChevronDown size={20} className="text-surface-400" />
                  )}
                </div>
              </div>
              
              {isOpen && (
                <div className="px-6 pb-6 mt-4 border-t border-surface-100 dark:border-surface-800">
                  <div className="pt-4 space-y-3">
                    {section.content.map((item, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <div className="w-5 h-5 rounded-full bg-success-100 dark:bg-success-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <CheckCircle size={12} className="text-success-600 dark:text-success-400" />
                        </div>
                        <p className="text-sm text-surface-700 dark:text-surface-300 leading-relaxed">
                          {item}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Quick Help Section */}
      <div className="card bg-gradient-to-r from-primary-50 to-primary-50 dark:from-primary-900/20 dark:to-primary-950/30 border-primary-200 dark:border-primary-800">
        <div className="card-body">
          <h3 className="font-semibold text-surface-900 dark:text-white mb-4 flex items-center gap-2">
            <Info size={20} className="text-primary-900 dark:text-primary-400" />
            Butuh Bantuan?
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3 p-4 bg-white dark:bg-surface-800 rounded-lg">
              <div className="w-8 h-8 bg-primary-100 dark:bg-primary-950/50 rounded-lg flex items-center justify-center">
                <Users size={16} className="text-primary-900 dark:text-primary-400" />
              </div>
              <div>
                <p className="font-medium text-sm text-surface-900 dark:text-white">Tim Dukungan</p>
                <p className="text-xs text-surface-600 dark:text-surface-400">Hubungi admin untuk bantuan</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-white dark:bg-surface-800 rounded-lg">
              <div className="w-8 h-8 bg-success-100 dark:bg-success-900/30 rounded-lg flex items-center justify-center">
                <FileText size={16} className="text-success-600 dark:text-success-400" />
              </div>
              <div>
                <p className="font-medium text-sm text-surface-900 dark:text-white">Pusat Bantuan</p>
                <p className="text-xs text-surface-600 dark:text-surface-400">Lihat FAQ dan artikel bantuan</p>
              </div>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-primary-200 dark:border-primary-800">
            <p className="text-sm text-surface-600 dark:text-surface-400 mb-3">
              Jika Anda mengalami masalah, jangan ragu untuk menghubungi tim dukungan kami.
            </p>
            <button className="btn btn-primary btn-sm">
              <HelpCircle size={16} className="mr-2" />
              Hubungi Dukungan
            </button>
          </div>
        </div>
      </div>

      {/* Version Info */}
      <div className="text-center text-xs text-surface-400 dark:text-surface-500 pb-4">
        <p>Stokku v1.0.0 - Panduan Penggunaan Terakhir diperbarui: 24 Juni 2026</p>
      </div>
    </div>
  );
}